import { motion, AnimatePresence, useDragControls, type PanInfo } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { X, Heart, Snowflake, AlertTriangle, RefreshCw } from 'lucide-react'

const F = 'Roboto, sans-serif'

interface Props {
  open: boolean
  onClose: () => void
  lives: number
  freezeActive: boolean
  freezeAvailable: boolean
  hoursRemaining?: number | null
}

/**
 * Объясняет клиенту, как работают жизни, заморозки и Last Chance.
 * Открывается тапом по сердечкам на главной.
 */
export function LivesInfoSheet({ open, onClose, lives, freezeActive, freezeAvailable, hoursRemaining }: Props) {
  const dragControls = useDragControls()
  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 80) onClose()
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 410,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="lives-info-sheet"
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            drag="y" dragListener={false} dragControls={dragControls}
            dragConstraints={{ top: 0 }} dragElastic={0.1}
            onDragEnd={handleDragEnd}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#F5F6FA',
              borderTopLeftRadius: 28, borderTopRightRadius: 28,
              paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
              maxHeight: '92vh',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div
              onPointerDown={(e) => dragControls.start(e)}
              style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4, cursor: 'grab', touchAction: 'none' }}
            >
              <SheetGrip onClose={onClose} />
            </div>

            <div style={{ position: 'relative', padding: '8px 20px 8px' }}>
              <button
                onClick={onClose}
                aria-label="Закрыть"
                style={{
                  position: 'absolute', top: 4, right: 16,
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.06)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={16} color="#6B7B8D" />
              </button>

              {/* Hero — текущее состояние */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingRight: 40 }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '8px 12px', borderRadius: 14,
                  background: 'rgba(255,59,48,0.10)',
                  border: '1px solid rgba(255,59,48,0.30)',
                }}>
                  {[1, 2, 3].map((i) => {
                    const active = lives >= i
                    return (
                      <Heart
                        key={i}
                        size={18}
                        color={active ? '#FF3B30' : 'rgba(26,58,92,0.20)'}
                        fill={active ? '#FF3B30' : 'transparent'}
                        strokeWidth={2.4}
                      />
                    )
                  })}
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#1C2D41', marginLeft: 4 }}>
                    {lives}/3
                  </span>
                </div>
                {freezeActive && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '8px 12px', borderRadius: 14,
                    background: 'rgba(74,159,229,0.10)',
                    border: '1px solid rgba(74,159,229,0.32)',
                  }}>
                    <Snowflake size={16} color="#1F8FE5" />
                    <span style={{ fontSize: 12, fontWeight: 800, color: '#1F4F94' }}>
                      {hoursRemaining != null ? `${Math.ceil(hoursRemaining)} ч` : 'Заморозка'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '6px 16px 0', minHeight: 0 }}>
              {/* Что это такое */}
              <div style={{
                background: 'rgba(108,92,231,0.06)',
                border: '1px solid rgba(108,92,231,0.16)',
                borderRadius: 14, padding: 12,
                marginBottom: 12,
              }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1C2D41', marginBottom: 4 }}>
                  Жизни и заморозки
                </div>
                <p style={{ fontSize: 12, color: '#3D4A5C', margin: 0, lineHeight: 1.55 }}>
                  Стартовых жизней — 3 ❤️. Плюс 1 автоматическая заморозка ❄️ на программу. Жизни нужны, чтобы оставаться в «Выживании»: при 0 жизней ты выбываешь из челленджа, но доступ к программе сохраняется, а стаж дней в режиме фиксируется.
                </p>
              </div>

              {/* Таймлайн потери жизней */}
              <div style={{
                background: 'white',
                border: '1px solid rgba(26,58,92,0.06)',
                borderRadius: 14, padding: 12,
                marginBottom: 12,
              }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#1C2D41', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <RefreshCw size={13} color="#6C5CE7" /> Как тратятся жизни
                </div>

                {[
                  { hours: '0–34 ч', icon: <Heart size={14} color="#FF3B30" fill="#FF3B30" />, text: 'Всё в порядке. Жизни и стаж не трогаем.', color: '#34C759' },
                  { hours: '34 ч простоя', icon: <Snowflake size={14} color="#1F8FE5" />, text: 'Если есть заморозка — активируется автоматически (+24 ч защиты). Если нет — сразу −1 жизнь.', color: '#1F8FE5' },
                  // #309: −1 жизнь НЕ сбивает стаж — пропуск «оплачен» жизнью (#290).
                  // Режим обнуляется только когда сгорели все жизни.
                  { hours: '58 ч (34 + 24)', icon: <AlertTriangle size={14} color="#FF9500" />, text: 'Заморозка закончилась без активности → −1 жизнь. Стаж дней сохраняется.', color: '#FF9500' },
                  { hours: 'каждые +24 ч', icon: <AlertTriangle size={14} color="#FF3B30" />, text: 'Дальше каждые 24 ч простоя — ещё −1 жизнь.', color: '#FF3B30' },
                  { hours: '0 жизней', icon: <Heart size={14} color="rgba(26,58,92,0.2)" />, text: 'Стаж дней в режиме обнуляется. «Последний шанс»: выполни любое действие — и одна жизнь восстановится.', color: '#6C5CE7' },
                ].map((row, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '8px 0',
                    borderTop: i === 0 ? 'none' : '1px solid rgba(26,58,92,0.06)',
                  }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: 8,
                      background: `${row.color}15`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {row.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: row.color, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        {row.hours}
                      </div>
                      <div style={{ fontSize: 12, color: '#1C2D41', marginTop: 2, lineHeight: 1.4 }}>
                        {row.text}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Магазин */}
              <div style={{
                background: 'rgba(255,149,0,0.06)',
                border: '1px solid rgba(255,149,0,0.18)',
                borderRadius: 14, padding: 12,
                marginBottom: 16,
              }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#B45309', marginBottom: 6 }}>
                  В Магазине за TuCoin
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#6B5C40', lineHeight: 1.55 }}>
                  <li>Купить жизнь (+1, максимум 3) — стаж дней не восстанавливается.</li>
                  <li>Купить заморозку (+1 к автоматическим отсрочкам).</li>
                </ul>
                <p style={{ fontSize: 11, color: '#9B7B40', margin: '8px 0 0', lineHeight: 1.45 }}>
                  Сейчас доступно заморозок: <b>{freezeAvailable ? '1 авто' : '0'}</b>.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
